/**
 * Token Error Handler
 * Utility functions for handling token-related errors based on errorCode
 */

import { membershipServices, ErrorTrackingAPI } from '@services'
import { authManager } from '@auth'
import { customToast } from '@common'
import {
    isTokenError,
    isSecurityThreat,
    shouldNavigateToEmailVerification,
    shouldNavigateToPasswordReset,
    isAccountStatusError,
    REFRESH_TOKEN_ERROR_CODES,
    LOGIN_ERROR_CODES,
} from './errorCodes'

/**
 * Handle token error based on errorCode
 * @param {Object} result - API result containing errorCode and error message
 * @param {string} result.errorCode - Error code
 * @param {string} result.error - Error message
 * @param {Object} options - Additional options
 * @param {Function} options.navigate - Navigation function (optional)
 * @param {Function} options.onSecurityThreat - Callback for security threats (optional)
 * @returns {Object} Handling result with action taken
 */
export const handleTokenError = (result, options = {}) => {
    const { errorCode, error } = result
    const { navigate, onSecurityThreat } = options

    if (!errorCode) {
        return { handled: false, action: 'none' }
    }

    // Handle security threats (TOKEN_REUSE_DETECTED)
    if (isSecurityThreat(errorCode)) {
        return handleSecurityThreat(error, { navigate, onSecurityThreat })
    }

    // Handle token-related errors
    if (isTokenError(errorCode)) {
        return handleTokenErrorAndLogout(error, errorCode, { navigate })
    }

    // Handle email verification required
    if (shouldNavigateToEmailVerification(errorCode)) {
        return handleEmailVerificationRequired({ navigate })
    }

    // Handle password reset required
    if (shouldNavigateToPasswordReset(errorCode)) {
        return handlePasswordResetRequired({ navigate })
    }

    // Handle account status errors
    if (isAccountStatusError(errorCode)) {
        return handleAccountStatusError(error, errorCode)
    }

    // Default: show error message
    return handleGenericError(error, errorCode)
}

/**
 * Handle security threat (TOKEN_REUSE_DETECTED)
 * @param {string} error - Error message
 * @param {Object} options - Options
 * @returns {Object} Handling result
 */
const handleSecurityThreat = (error, options = {}) => {
    const { navigate, onSecurityThreat } = options

    // Clear all tokens
    membershipServices.clearTokens()

    // Show security alert
    customToast.error('Security threat detected. All sessions have been revoked. Please login again.', {
        duration: 5000,
        icon: <i className="bx bx-sm bx-shield-x text-danger"></i>,
    })

    // Trigger logout callbacks
    authManager.logout(true) // Silent logout to avoid duplicate toast

    // Call custom security threat handler if provided
    if (onSecurityThreat) {
        try {
            onSecurityThreat(error)
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'TokenErrorHandler', 'handleSecurityThreat', 'callback')
        }
    }

    // Navigate to login if navigate function provided
    if (navigate && typeof navigate === 'function') {
        setTimeout(() => {
            if (navigate && typeof navigate === 'function') {
                navigate('/')
            }
        }, 100)
    }

    return { handled: true, action: 'security_threat_logout' }
}

/**
 * Handle token error and logout
 * @param {string} error - Error message
 * @param {string} errorCode - Error code
 * @param {Object} options - Options
 * @returns {Object} Handling result
 */
const handleTokenErrorAndLogout = (error, errorCode, options = {}) => {
    const { navigate } = options

    // Clear tokens
    membershipServices.clearTokens()

    // Show error message
    customToast.error(error || 'Session expired. Please login again.', {
        duration: 3000,
        icon: <i className="bx bx-sm bx-error text-danger"></i>,
    })

    // Trigger logout
    authManager.logout(true) // Silent logout to avoid duplicate toast

    // Navigate to login if navigate function provided
    if (navigate && typeof navigate === 'function') {
        setTimeout(() => {
            if (navigate && typeof navigate === 'function') {
                navigate('/')
            }
        }, 100)
    }

    return { handled: true, action: 'token_error_logout' }
}

/**
 * Handle email verification required
 * @param {Object} options - Options
 * @returns {Object} Handling result
 */
const handleEmailVerificationRequired = (options = {}) => {
    const { navigate } = options

    // This will be handled by the calling component
    // which has access to email and can navigate appropriately
    return { handled: true, action: 'email_verification_required', navigate }
}

/**
 * Handle password reset required
 * @param {Object} options - Options
 * @returns {Object} Handling result
 */
const handlePasswordResetRequired = (options = {}) => {
    const { navigate } = options

    // Navigate to password reset if navigate function provided
    if (navigate && typeof navigate === 'function') {
        navigate('/password-reset')
    }

    return { handled: true, action: 'password_reset_required' }
}

/**
 * Handle account status error
 * @param {string} error - Error message
 * @param {string} errorCode - Error code
 * @returns {Object} Handling result
 */
const handleAccountStatusError = (error, errorCode) => {
    // Show account status message
    customToast.warning(error || 'Your account has been suspended. Please contact support.', {
        duration: 5000,
        icon: <i className="bx bx-sm bx-user-x text-warning"></i>,
    })

    return { handled: true, action: 'account_status_error' }
}

/**
 * Handle generic error
 * @param {string} error - Error message
 * @param {string} errorCode - Error code
 * @returns {Object} Handling result
 */
const handleGenericError = (error, errorCode) => {
    // Show generic error message
    customToast.error(error || 'An error occurred. Please try again.', {
        duration: 4000,
        icon: <i className="bx bx-sm bx-error text-danger"></i>,
    })

    return { handled: true, action: 'generic_error' }
}

/**
 * Check if error should be handled silently (no user notification)
 * @param {string} errorCode - Error code
 * @returns {boolean} Whether error should be handled silently
 */
export const shouldHandleSilently = errorCode => {
    // Some errors might be handled silently in certain contexts
    // For example, during automatic token refresh
    return (
        errorCode === REFRESH_TOKEN_ERROR_CODES.REFRESH_TOKEN_EXPIRED ||
        errorCode === REFRESH_TOKEN_ERROR_CODES.REFRESH_TOKEN_REVOKED
    )
}

/**
 * Get user-friendly error message based on errorCode
 * @param {string} errorCode - Error code
 * @param {string} defaultMessage - Default error message
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (errorCode, defaultMessage = 'An error occurred') => {
    const errorMessages = {
        // Login error messages
        [LOGIN_ERROR_CODES.INVALID_CREDENTIALS]: 'Invalid email or password.',
        [LOGIN_ERROR_CODES.EMAIL_NOT_VERIFIED]: 'Please verify your email before logging in.',
        [LOGIN_ERROR_CODES.ACCOUNT_SUSPENDED]: 'Your account has been suspended.',
        [LOGIN_ERROR_CODES.ACCOUNT_ARCHIVED]: 'Your account has been archived.',
        [LOGIN_ERROR_CODES.PASSWORD_NOT_SET]: 'Password not set. Please reset your password.',
        [LOGIN_ERROR_CODES.INVALID_EMAIL_FORMAT]: 'Invalid email format.',
        [LOGIN_ERROR_CODES.MEMBER_INACTIVE]: 'Your account is inactive.',

        // Refresh token error messages
        [REFRESH_TOKEN_ERROR_CODES.INVALID_REFRESH_TOKEN]: 'Invalid refresh token.',
        [REFRESH_TOKEN_ERROR_CODES.REFRESH_TOKEN_EXPIRED]: 'Session expired. Please login again.',
        [REFRESH_TOKEN_ERROR_CODES.REFRESH_TOKEN_REVOKED]: 'Session revoked. Please login again.',
        [REFRESH_TOKEN_ERROR_CODES.REFRESH_TOKEN_NOT_FOUND]: 'Session not found. Please login again.',
        [REFRESH_TOKEN_ERROR_CODES.TOKEN_REUSE_DETECTED]: 'Security threat detected. All sessions have been revoked.',
        [REFRESH_TOKEN_ERROR_CODES.MEMBER_NOT_FOUND]: 'Member not found.',
        [REFRESH_TOKEN_ERROR_CODES.MEMBER_INACTIVE]: 'Your account is inactive.',
    }

    return errorMessages[errorCode] || defaultMessage
}
